import React from "react"
import { useDispatch, useSelector } from "react-redux"
import { Alert, Button } from "@mui/material"

import { useLocation, useParams } from "react-router-dom"
import { FEATURE_FLAGS, phoneNumber } from "../../../config"
import AvailabilityPicker from "./AvailabilityPicker"
import Section from "../product-marketing/Section"
import { RootState } from "../../../store/configureStore"
import LocationSearchInput from "../LocationSearchInput"
import LessonTypePicker from "./LessonTypePicker"
import {
  searchAddressToPoints,
  setDefaultLessonTypeForState,
  setMeetingPoints,
  setSelectedLessonType,
} from "../../../actions/searchActions"
import makeCityFromSlug from "../../../utils/makeCityFromSlug"
import { lessonTypes } from "../../../content"
import PackageSelectWrapper from "../PackageSelectWrapper"
import { LessonTypeName, MeetingType } from "../../../types"
import { LessonCouponBanner } from "../LessonCouponBanner"
import { useStudentMinutes } from "../../../hooks/useStudentMinutes"
import { useProcessBookingAddress } from "../../../hooks/useProcessBookingAddress"

/**
 * This sits in between users of the AvailabilityPicker and the Picker
 * itself. Its goal is to provide a layer of abstraction to make decision
 * between showing an address input, something hard coded, or the Picker, etc.
 */

interface Props {
  lessonTypeName: string
  city?: string
  doRedirect: boolean
  instructorId?: string
}

const SearchLogicWrapper = ({
  lessonTypeName,
  city,
  doRedirect,
  instructorId,
}: Props) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { selectedLessonType } = useSelector((state: RootState) => state.search)
  const { id: urlCity, state: urlState } = useParams<{
    id?: string
    state?: string
  }>()
  const searchLocation = useSelector((state: RootState) => state.location)
  const { profile, auth } = useSelector((state: RootState) => state.firebase)
  const [hasChosenLessonType, setHasChosenLessonType] = React.useState(false)
  const [isAddressIncomplete, setAddressIncomplete] = React.useState(false)
  const [isURLSearch, setIsURLSearch] = React.useState(false)

  const studentMinutes = useStudentMinutes(profile.isAdmin ? null : auth.uid)

  const searchParams = new URLSearchParams(location.search)
  const addressParam = searchParams.get("address")
  const lessonTypeParam = searchParams.get("type") ?? undefined

  const processAddress = useProcessBookingAddress({
    doRedirect: true,
    lessonType: lessonTypeParam,
    incompleteAddressCallback: (incomplete) => {
      setAddressIncomplete(incomplete)

      if (incomplete) {
        setHasChosenLessonType(false)
      } else {
        setIsURLSearch(true)
        if (lessonTypeParam) {
          dispatch(setSelectedLessonType(lessonTypes[lessonTypeParam]))
          setHasChosenLessonType(true)
        }
      }
    },
  })

  React.useEffect(() => {
    if (addressParam) {
      processAddress(addressParam)
    }
  }, [addressParam, processAddress])

  React.useEffect(() => {
    if (!lessonTypeName) {
      dispatch(
        setDefaultLessonTypeForState(
          searchLocation.searchState?.toUpperCase() ||
            urlState?.toUpperCase() ||
            "CA",
        ),
      )
    } else {
      dispatch(setSelectedLessonType(lessonTypes[lessonTypeName]))
    }
  }, [dispatch, lessonTypeName, urlState, searchLocation.searchState])

  React.useEffect(() => {
    if (
      !searchLocation.searchAddress ||
      !searchLocation.searchState ||
      !searchLocation.stateConfig ||
      !searchLocation.searchGeocode
    )
      return
    searchAddressToPoints(
      searchLocation.searchAddress,
      searchLocation.searchGeocode,
      searchLocation.stateConfig,
      searchLocation.searchState,
    )
      .then((points) => {
        if (points.length === 0) return
        dispatch(setMeetingPoints(points))
      })
      .catch(() => {
        // This can happen when the search geocode momentarily goes empty.
        // We don't want to fatal the whole page when this happens.
        // noop
      })
  }, [
    dispatch,
    searchLocation.searchAddress,
    searchLocation.searchGeocode,
    searchLocation.searchState,
    searchLocation.stateConfig,
  ])

  React.useEffect(() => {
    // If they have observation minutes in their account, we automatically
    // want to set their lesson type to shared.

    if (
      isURLSearch ||
      !searchLocation.searchAddress ||
      (studentMinutes?.observationMinutesRemaining || 0) <= 0
    ) {
      return
    }

    if (!selectedLessonType) {
      dispatch(setSelectedLessonType(lessonTypes[LessonTypeName.Shared]))
      setHasChosenLessonType(true)
    }
  }, [
    studentMinutes,
    dispatch,
    searchLocation.searchAddress,
    isURLSearch,
    selectedLessonType,
  ])

  const maybeRenderResetLessonType = () => {
    if (!hasChosenLessonType) return null
    if ((studentMinutes?.observationMinutesRemaining || 0) > 0) {
      return null
    }
    return (
      <Button
        className="button-as-link"
        style={{ textTransform: "none", marginBottom: 10 }}
        onClick={() => setHasChosenLessonType(false)}
      >
        &#171; Change Lesson Type
      </Button>
    )
  }

  const renderAvailabilityPicker = () => (
    <>
      <aside>
        <LessonCouponBanner address={searchLocation.searchAddress} />
      </aside>
      <Section
        head={`Book Your ${city || ""} Driving Lesson`}
        subhead="Scroll down for availability."
      >
        {maybeRenderResetLessonType()}
        <AvailabilityPicker instructorId={instructorId} />
      </Section>
    </>
  )

  const getPlaceholderAddress = () => {
    if (profile.address) {
      return `123 Main St, ${profile.address.city}, ${profile.address.state}`
    }
    if (urlCity && urlState) {
      return `123 Main St, ${makeCityFromSlug(urlCity)}, ${urlState.toLocaleUpperCase()}`
    }
    return "123 Main St, Houston, TX"
  }

  const renderAddressSearch = () => {
    const head = `Enter your address for availability ${city ? `in ${city}` : ""}`
    const subhead =
      "Availability varies by location. Your address is used to find the latest information."
    return (
      <Section head={head} subhead={subhead}>
        {isAddressIncomplete ? (
          <Alert severity="error">
            It looks like you are missing a street address. Please enter a full
            address like &ldquo;123 Main Street, Pasadena, CA&rdquo;, rather
            than just a city or neighborhood.
          </Alert>
        ) : null}
        <LocationSearchInput
          placeholder={`Example: ${getPlaceholderAddress()}`}
          incompleteAddressCallback={(newVal: boolean) =>
            setAddressIncomplete(newVal)
          }
          doRedirect={doRedirect}
        />
        <div style={{ fontSize: 12, marginTop: 5 }}>
          <strong>Privacy notice:</strong> We do not store or use this search
          address for marketing purposes.
        </div>
      </Section>
    )
  }

  const renderCheckoutFlowPackages = () => (
    <Section
      head="Choose a package to purchase your lessons"
      subhead={`Once your lessons are purchased, call us at ${phoneNumber} to book.`}
    >
      {searchLocation.searchState?.toLowerCase() === "tx"
        ? maybeRenderResetLessonType()
        : null}
      <PackageSelectWrapper
        address={searchLocation.searchAddress ?? ""}
        defaultPackageFilter={selectedLessonType.name}
        showCallSupportDialog
        showPackageFilter={false}
        meetingType={MeetingType.MeetingPoint} // This is only used for shared lessons currently
      />
    </Section>
  )

  const renderLessonTypeSelector = () => (
    <Section
      head="What type of lesson are you looking for?"
      subhead="We have different types of lessons in your state. Please choose one to continue."
    >
      <LessonTypePicker setHasChosenLessonType={setHasChosenLessonType} />
    </Section>
  )

  if (
    !searchLocation.searchAddress ||
    !searchLocation.searchState ||
    !searchLocation.searchCounty ||
    isAddressIncomplete ||
    !searchLocation.stateConfig
  ) {
    return renderAddressSearch()
  }

  if (searchLocation.stateConfig.isShareableByDefault) {
    // It's probably Texas, so we need to do some extra checks.
    if (!hasChosenLessonType) {
      if ((studentMinutes?.observationMinutesRemaining || 0) > 0) {
        setHasChosenLessonType(true)
        dispatch(setSelectedLessonType(lessonTypes[LessonTypeName.Shared]))
        return null
      }
      return renderLessonTypeSelector()
    }

    if (
      selectedLessonType.name === LessonTypeName.Shared &&
      !profile.isAdmin &&
      !FEATURE_FLAGS.allowSharedLessonsSelfBooking.isEnabled(
        searchLocation.searchState || "",
      )
    ) {
      return renderCheckoutFlowPackages()
    }
  }
  // else it's okay to do the avail picker.
  return renderAvailabilityPicker()
}

export default SearchLogicWrapper
